import { DropdownItem } from '@atlaskit/dropdown-menu';
import { Radio } from '@atlaskit/radio';
import { token } from '@atlaskit/tokens';
import { css } from '@compiled/react';
import React from 'react';

import { trackUserActionClicked } from '../../../../../../common/utils/analytics/analytics';
import { type Theme, useTheme } from '../../../../../context/ThemeContext';
import { DropdownItemLabel } from '../DropdownItems.styles';

import DarkIcon from './assets/dark.svg';
import LightIcon from './assets/light.svg';
import MatchBrowserIcon from './assets/match-browser.svg';

const themeOptionStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: token('space.150'),
});

const themeOptionLabelStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: token('space.100'),
  flex: 1,
});

const iconStyles = css({
  width: '4rem',
  height: '3rem',
  flexShrink: 0,
  borderRadius: token('radius.small'),
  border: `1px solid ${token('color.border')}`,
});

const THEME_OPTIONS = [
  {
    value: 'light' as Theme,
    label: 'Light',
    icon: <img src={LightIcon} alt="Light theme" css={iconStyles} />,
  },
  {
    value: 'dark' as Theme,
    label: 'Dark',
    icon: <img src={DarkIcon} alt="Dark theme" css={iconStyles} />,
  },
  {
    value: 'auto' as Theme,
    label: 'Match browser',
    icon: <img src={MatchBrowserIcon} alt="Match browser theme" css={iconStyles} />,
  },
];

const ThemeOption = ({
  value,
  label,
  icon,
  isSelected,
  onSelect,
}: {
  value: Theme;
  label: string;
  icon: React.ReactNode;
  isSelected: boolean;
  onSelect: (theme: Theme) => void;
}) => {
  const handleClick = React.useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onSelect(value);
      trackUserActionClicked(`Theme switched to ${value}`);
    },
    [onSelect, value]
  );

  return (
    <DropdownItem onClick={handleClick}>
      <div css={themeOptionStyles}>
        <Radio
          isChecked={isSelected}
          value={value}
          name="theme-selector"
          onChange={() => onSelect(value)}
        />
        <div css={themeOptionLabelStyles}>
          {icon}
          <DropdownItemLabel>{label}</DropdownItemLabel>
        </div>
      </div>
    </DropdownItem>
  );
};

export const ThemeToggleItem = () => {
  const { theme, setTheme } = useTheme();

  return (
    <>
      {THEME_OPTIONS.map((option) => (
        <ThemeOption
          key={option.value}
          value={option.value}
          label={option.label}
          icon={option.icon}
          isSelected={theme === option.value}
          onSelect={setTheme}
        />
      ))}
    </>
  );
};
